import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { fetchRequestDetails } from '../../util/api';

import { Heading } from '../Heading/Heading';

import css from './RequestPrivateDetails.module.css';

/**
 * Loads a request's private details (the homeowner's description and photos) for the
 * signed-in viewer. Client-side only, on purpose: the details must never be part of
 * server-rendered HTML, which anyone can read. The server decides who may see them
 * (the author and the pros they chose); everyone else gets status "notShared".
 *
 * @param {Object} params
 * @param {string} params.listingId request listing uuid
 * @param {boolean} params.enabled fetch only for a signed-in viewer of a request listing
 * @returns {{status: 'idle'|'loading'|'loaded'|'notShared'|'error', details: Object|null}}
 */
export const useRequestDetails = ({ listingId, enabled }) => {
  const [state, setState] = useState({ status: 'idle', details: null });

  useEffect(() => {
    if (!enabled || !listingId) {
      setState({ status: 'idle', details: null });
      return undefined;
    }
    let cancelled = false;
    setState({ status: 'loading', details: null });
    fetchRequestDetails(listingId)
      .then(details => {
        if (!cancelled) setState({ status: 'loaded', details });
      })
      .catch(e => {
        if (cancelled) return;
        const notShared = e.status === 401 || e.status === 404;
        setState({ status: notShared ? 'notShared' : 'error', details: null });
      });
    return () => {
      cancelled = true;
    };
  }, [listingId, enabled]);

  return state;
};

/**
 * The private part of a request listing page: the homeowner's own description and
 * photos for the people allowed to see them, and a plain note for everyone else.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.state from useRequestDetails
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
const LockIcon = () => (
  <svg
    className={css.lockIcon}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

const RequestPrivateDetails = ({ state, className }) => {
  const intl = useIntl();
  const { status, details } = state;
  const classes = classNames(css.root, className);

  if (status === 'loading') {
    return (
      <section className={classes} aria-busy="true">
        <p className={css.note}>
          <FormattedMessage id="RequestPrivateDetails.loading" />
        </p>
      </section>
    );
  }

  if (status === 'error') {
    return (
      <section className={classes}>
        <p className={css.note} role="status">
          <FormattedMessage id="RequestPrivateDetails.error" />
        </p>
      </section>
    );
  }

  // Everyone the homeowner hasn't picked (and signed-out visitors).
  if (status !== 'loaded') {
    return (
      <section className={classes}>
        <Heading as="h2" rootClassName={css.title}>
          <FormattedMessage id="RequestPrivateDetails.title" />
        </Heading>
        <p className={css.locked}>
          <LockIcon />
          <FormattedMessage id="RequestPrivateDetails.lockedBody" />
        </p>
      </section>
    );
  }

  // Just what the homeowner wrote and their photos. The name ("Requested by"), the
  // address and the "submit an offer to unlock the address" note live elsewhere on the
  // page (under the title and under Location).
  const { description, photos = [] } = details || {};
  return (
    <section className={classes} aria-labelledby="request-private-details-title">
      <Heading as="h2" rootClassName={css.title} id="request-private-details-title">
        <FormattedMessage id="RequestPrivateDetails.title" />
      </Heading>
      {description ? (
        <p className={css.description}>{description}</p>
      ) : (
        <p className={css.note}>
          <FormattedMessage id="RequestPrivateDetails.noDescription" />
        </p>
      )}
      {photos.length > 0 ? (
        <ul className={css.photos}>
          {photos.map((photo, i) => (
            <li key={photo.url} className={css.photo}>
              <a href={photo.url} target="_blank" rel="noopener noreferrer">
                <img
                  className={css.photoImg}
                  src={photo.url}
                  alt={intl.formatMessage(
                    { id: 'RequestPrivateDetails.photoAlt' },
                    { number: i + 1, count: photos.length }
                  )}
                  loading="lazy"
                />
              </a>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
};

export default RequestPrivateDetails;
